import { AnyAction } from 'redux';
import {
  GlobalActions,
  GlobalFeatureToggleReceivedAction,
  GlobalRegexReceivedAction,
  GlobalRoleDeterminedAction,
  ProduktDeleted,
  TgvEingabeTarget,
} from '@ps20-frontend/shop-integration-redux';
import { App, BaseTransientState, Rolle } from '@ps20-frontend/frontend-models';
import { areTkvListsLoaded } from '../models/catalog';
import { childKeyOf } from '../models/childKeys';
import { TgvProduktSnapshot } from '../models/angebot.model';
import {
  answeredKeysOf,
  withoutErrorCodes,
  withoutKeysOfIssue,
  withoutPricingIssueKeys,
  withStatusOfKey,
} from '../models/pricingIssue';
import { TgvKatalog } from '../models/tiergruppe.model';
import {
  ChildPricingForwardedPayload,
  PricingIssueAnsweredPayload,
  PricingIssueReportedPayload,
  TgvActions,
  TgvPricingIssueKind,
  TgvPricingStatus,
} from '../tgvActions';

export interface TgvChildrenState {
  creating: string[];
  pendingDeletion: string[];
  registryVersion: number;
}

export interface TgvPricingEntry {
  status: TgvPricingStatus;
  attempts: number;
  requestId?: string;
  reloadOpen?: boolean;
}

export interface TgvAngebotState {
  current: string | null;
  editedKey: string | null;
  visited: string[];
  summaryExpanded: boolean;
  adoptionError: boolean;
  copyInProgress: boolean;
}

export interface TgvPricingIssue {
  kind: TgvPricingIssueKind;
  keys: string[];
  errorCodes: Record<string, string>;
}

export type TransientState = BaseTransientState &
  Rolle & {
    showErrors: boolean;
    takeOverRunning: boolean;
    eingabeTarget: TgvEingabeTarget | null;
    katalogError: boolean;
    juristisch: boolean;
    runFailure: string | null;
    pricingIssue: TgvPricingIssue | null;
    produkte: TgvProduktSnapshot[];
    katalog: TgvKatalog;
    children: TgvChildrenState;
    pricing: Record<string, TgvPricingEntry>;
    angebot: TgvAngebotState;
  };

const withoutKeys = (list: string[], keys: string[]): string[] =>
  list.filter(key => !keys.includes(key));

const withKeys = (list: string[], keys: string[]): string[] => [
  ...list,
  ...keys.filter((key, index) => !list.includes(key) && keys.indexOf(key) === index),
];

const hasFailedOffer = (sectionState: TransientState, issue: TgvPricingIssue): boolean =>
  issue.keys.some(key => sectionState.pricing[key]?.status === 'failed');

const withReportedPricingIssue = (
  sectionState: TransientState,
  { kind, keys, errorCodes }: PricingIssueReportedPayload,
): TransientState => {
  const current = sectionState.pricingIssue;
  if (kind === 'failed') {
    return {
      ...sectionState,
      pricingIssue:
        current?.kind === 'failed'
          ? {
              kind,
              keys: withKeys(current.keys, keys),
              errorCodes: { ...withoutErrorCodes(current.errorCodes, keys), ...errorCodes },
            }
          : { kind, keys, errorCodes },
    };
  }
  return current?.kind === 'failed' && hasFailedOffer(sectionState, current)
    ? sectionState
    : { ...sectionState, pricingIssue: { kind, keys, errorCodes: {} } };
};

const withAnsweredPricingIssue = (
  sectionState: TransientState,
  payload: PricingIssueAnsweredPayload,
): TransientState => {
  const answered = answeredKeysOf(sectionState.pricingIssue, payload);
  if (answered.length === 0) {
    return sectionState;
  }
  const pricingIssue =
    payload.answer === 'leave'
      ? null
      : withoutPricingIssueKeys(sectionState.pricingIssue, answered);
  return { ...sectionState, pricingIssue };
};

const withWithdrawnPricingIssueKeys = (
  sectionState: TransientState,
  keys: string[],
): TransientState => {
  const pricingIssue = withoutKeysOfIssue(sectionState.pricingIssue, keys);
  return pricingIssue === sectionState.pricingIssue
    ? sectionState
    : { ...sectionState, pricingIssue };
};

const reloadOf = (entry: TgvPricingEntry | undefined): Pick<TgvPricingEntry, 'reloadOpen'> =>
  entry?.reloadOpen ? { reloadOpen: true } : {};

const withoutReloadMark = (entry: TgvPricingEntry): TgvPricingEntry => {
  const { reloadOpen: _reloadOpen, ...rest } = entry;
  return rest;
};

const withoutOpenReloads = (sectionState: TransientState): TransientState =>
  Object.values(sectionState.pricing).some(entry => entry.reloadOpen)
    ? {
        ...sectionState,
        pricing: Object.fromEntries(
          Object.entries(sectionState.pricing).map(([key, entry]) => [
            key,
            withoutReloadMark(entry),
          ]),
        ),
      }
    : sectionState;

const withRestoredSnapshot = (sectionState: TransientState, action: AnyAction): TransientState =>
  action.payload?.appId === App.TGV ? withoutOpenReloads(sectionState) : sectionState;

const withReloadEnded = (sectionState: TransientState, key: string): TransientState => {
  const entry = sectionState.pricing[key];
  if (!entry?.reloadOpen) {
    return sectionState;
  }
  return { ...sectionState, pricing: { ...sectionState.pricing, [key]: withoutReloadMark(entry) } };
};

const forwardedEntryOf = (
  entry: TgvPricingEntry | undefined,
  { requestId, reload }: ChildPricingForwardedPayload,
): TgvPricingEntry => ({
  status: 'running',
  attempts: 1,
  requestId,
  ...(reload ? { reloadOpen: true } : reloadOf(entry)),
});

const keepsEntryOnHandOver = (entry: TgvPricingEntry | undefined): boolean =>
  entry?.status === 'running' || entry?.reloadOpen === true;

const queuedWhileRunning = (sectionState: TransientState): TransientState => ({
  ...sectionState,
  pricing: Object.fromEntries(
    Object.entries(sectionState.pricing).map(([key, entry]) => [
      key,
      entry.status === 'running'
        ? { status: 'queued', attempts: entry.attempts, ...reloadOf(entry) }
        : entry,
    ]),
  ),
});

const withAttemptsReset = (sectionState: TransientState): TransientState => ({
  ...sectionState,
  pricing: Object.fromEntries(
    Object.entries(sectionState.pricing).map(([key, entry]) => [
      key,
      { ...entry, attempts: entry.status === 'running' ? 1 : 0 },
    ]),
  ),
});

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
): TransientState {
  switch (action.type) {
    case TgvActions.TGV_TAKE_OVER_STARTED:
      return { ...sectionState, takeOverRunning: true };
    case TgvActions.TGV_TAKE_OVER_ENDED:
      return { ...sectionState, takeOverRunning: false };
    case TgvActions.TGV_ZURUECK_ZUM_SHOP_MODAL_OPEN:
      return { ...sectionState, zurueckZumShopModalOpen: true };
    case TgvActions.TGV_ZURUECK_ZUM_SHOP_MODAL_CLOSE:
      return { ...sectionState, zurueckZumShopModalOpen: false };
    case TgvActions.TGV_FORM_SUBMITTED:
      return { ...sectionState, showErrors: true, runFailure: null };
    case TgvActions.TGV_ZUM_ANGEBOT_CLICKED:
      return {
        ...queuedWhileRunning(sectionState),
        showErrors: true,
        runFailure: null,
        pricingIssue: null,
        angebot: { ...sectionState.angebot, current: null, adoptionError: false },
      };
    case TgvActions.TGV_ANGEBOT_CLOSED:
      return { ...queuedWhileRunning(sectionState), pricingIssue: null };
    case GlobalActions.NAVIGATION_REQUESTED:
      return action.payload?.appId === App.SHOP_DASHBOARD
        ? { ...queuedWhileRunning(sectionState), pricingIssue: null }
        : sectionState;
    case GlobalActions.TIERGRUPPE_EINGABE_TARGET_SET:
      return {
        ...sectionState,
        eingabeTarget: action.payload,
        showErrors: false,
        angebot:
          action.payload?.target === 'OFFER'
            ? sectionState.angebot
            : { ...sectionState.angebot, editedKey: null },
      };
    case GlobalActions.CREATE_STRECKEN_SNAPSHOT:
      return action.payload?.appId === App.TGV
        ? { ...sectionState, angebot: { ...sectionState.angebot, editedKey: null } }
        : sectionState;
    case TgvActions.TGV_EINGABE_TARGET_CONSUMED:
      return { ...sectionState, eingabeTarget: null };
    case TgvActions.TGV_JURISTISCH_RECEIVED:
      return { ...sectionState, juristisch: action.payload === true };
    case TgvActions.TGV_RUN_FAILED:
      return { ...sectionState, runFailure: action.payload };
    case TgvActions.TGV_PRICING_ISSUE_REPORTED:
      return withReportedPricingIssue(sectionState, action.payload);
    case TgvActions.TGV_PRICING_ISSUE_ANSWERED:
      return withAnsweredPricingIssue(sectionState, action.payload);
    case TgvActions.TGV_PRICING_ISSUE_KEYS_WITHDRAWN:
      return withWithdrawnPricingIssueKeys(sectionState, action.payload);
    case TgvActions.TGV_PRODUKTE_RECEIVED:
      return { ...sectionState, produkte: action.payload };
    case TgvActions.TGV_KATALOG_REQUESTED:
      return { ...sectionState, katalogError: false };
    case TgvActions.TGV_KATALOG_REQUEST_FAILED:
      return { ...sectionState, katalogError: !areTkvListsLoaded(sectionState.katalog) };
    case TgvActions.TGV_KATALOG_RECEIVED: {
      const catalog = { ...sectionState.katalog, ...action.payload };
      return {
        ...sectionState,
        katalog: catalog,
        katalogError: sectionState.katalogError && !areTkvListsLoaded(catalog),
      };
    }
    case TgvActions.TGV_CHILD_CREATING_ADDED:
      return {
        ...sectionState,
        children: {
          ...sectionState.children,
          creating: withKeys(sectionState.children.creating, [action.payload]),
        },
      };
    case TgvActions.TGV_CHILD_CREATING_REMOVED:
      return {
        ...sectionState,
        children: {
          ...sectionState.children,
          creating: withoutKeys(sectionState.children.creating, [action.payload]),
        },
      };
    case TgvActions.TGV_PENDING_DELETION_MARKED:
      return {
        ...sectionState,
        children: {
          ...sectionState.children,
          pendingDeletion: withKeys(sectionState.children.pendingDeletion, action.payload),
        },
      };
    case TgvActions.TGV_PENDING_DELETION_CLEARED:
      return {
        ...sectionState,
        children: {
          ...sectionState.children,
          pendingDeletion: withoutKeys(sectionState.children.pendingDeletion, action.payload),
        },
      };
    case TgvActions.TGV_ANGEBOT_OFFER_OPENED:
      return {
        ...sectionState,
        angebot: {
          ...sectionState.angebot,
          current: action.payload,
          visited: withKeys(sectionState.angebot.visited, [action.payload]),
        },
      };
    case TgvActions.TGV_ANGEBOT_EDIT_MARKED:
      return { ...sectionState, angebot: { ...sectionState.angebot, editedKey: action.payload } };
    case TgvActions.TGV_PRICING_STATUS_CHANGED: {
      const { key, status, requestId } = action.payload;
      const attempts = sectionState.pricing[key]?.attempts ?? 0;
      const nextAttempts = status === 'running' ? attempts + 1 : attempts;
      const reload = reloadOf(sectionState.pricing[key]);
      return {
        ...sectionState,
        pricing: {
          ...sectionState.pricing,
          [key]:
            status === 'running' && requestId !== undefined
              ? { status, attempts: nextAttempts, requestId, ...reload }
              : { status, attempts: nextAttempts, ...reload },
        },
        pricingIssue: withStatusOfKey(sectionState.pricingIssue, key, status),
      };
    }
    case TgvActions.TGV_PRICING_ATTEMPTS_RESET: {
      const entry = sectionState.pricing[action.payload];
      if (!entry) {
        return sectionState;
      }
      return {
        ...sectionState,
        pricing: {
          ...sectionState.pricing,
          [action.payload]: { ...entry, attempts: entry.status === 'running' ? 1 : 0 },
        },
      };
    }
    case TgvActions.TGV_ANGEBOT_REQUESTED:
      return { ...withAttemptsReset(sectionState), pricingIssue: null };
    case TgvActions.TGV_ANGEBOT_OPENED:
      return {
        ...withAttemptsReset(sectionState),
        pricingIssue: null,
        angebot: {
          ...sectionState.angebot,
          adoptionError: false,
          summaryExpanded: false,
          copyInProgress: false,
        },
      };
    case TgvActions.TGV_ANGEBOT_OFFERS_VISITED:
      return {
        ...sectionState,
        angebot: {
          ...sectionState.angebot,
          visited: withKeys(sectionState.angebot.visited, action.payload),
        },
      };
    case TgvActions.TGV_ANGEBOT_SUMMARY_TOGGLED:
      return {
        ...sectionState,
        angebot: {
          ...sectionState.angebot,
          summaryExpanded: !sectionState.angebot.summaryExpanded,
        },
      };
    case TgvActions.TGV_ANGEBOT_ADOPTION_BLOCKED:
      return { ...sectionState, angebot: { ...sectionState.angebot, adoptionError: true } };
    case TgvActions.TGV_INS_DASHBOARD_CLICKED:
      return { ...sectionState, angebot: { ...sectionState.angebot, adoptionError: false } };
    case TgvActions.TGV_ANGEBOT_COPY_TO_ALL_STARTED:
      return { ...sectionState, angebot: { ...sectionState.angebot, copyInProgress: true } };
    case TgvActions.TGV_ANGEBOT_COPY_TO_ALL_FINISHED:
      return { ...sectionState, angebot: { ...sectionState.angebot, copyInProgress: false } };
    case TgvActions.TGV_CHILD_PRICING_FORWARDED:
      return {
        ...sectionState,
        pricing: {
          ...sectionState.pricing,
          [action.payload.key]: forwardedEntryOf(
            sectionState.pricing[action.payload.key],
            action.payload,
          ),
        },
        pricingIssue: withStatusOfKey(sectionState.pricingIssue, action.payload.key, 'running'),
      };
    case TgvActions.TGV_CHILD_PRICING_FORWARD_QUEUED: {
      const { key } = action.payload;
      const entry = sectionState.pricing[key];
      if (keepsEntryOnHandOver(entry)) {
        return sectionState;
      }
      return {
        ...sectionState,
        pricing: {
          ...sectionState.pricing,
          [key]: { status: 'queued', attempts: entry?.attempts ?? 0 },
        },
        pricingIssue: withStatusOfKey(sectionState.pricingIssue, key, 'queued'),
      };
    }
    case TgvActions.TGV_PRODUCT_RELOAD_ENDED:
      return withReloadEnded(sectionState, action.payload);
    case GlobalActions.RESTORE_STRECKEN_SNAPSHOT:
      return withRestoredSnapshot(sectionState, action);
    case GlobalActions.PRODUKT_DELETED: {
      const deleted: ProduktDeleted['angebotsIds'] = action.payload?.angebotsIds ?? [];
      const deletedKeys = deleted.map(childKeyOf).filter((key): key is string => key !== null);
      return {
        ...sectionState,
        children: {
          ...sectionState.children,
          pendingDeletion: withoutKeys(sectionState.children.pendingDeletion, deletedKeys),
        },
      };
    }
    case GlobalActions.ROLE_DETERMINED:
      return { ...sectionState, role: (action as GlobalRoleDeterminedAction).payload.role };
    case GlobalActions.FEATURE_TOGGLE_RECEIVED:
      return {
        ...sectionState,
        featureToggles: (action as GlobalFeatureToggleReceivedAction).payload,
      };
    case GlobalActions.REGEXPS_RECEIVED:
      return { ...sectionState, regexps: (action as GlobalRegexReceivedAction).payload };
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED:
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    default:
      return sectionState;
  }
};
